import React, {
  FC,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import './daily-briefing-section.scss';
import './travel-timezone-section.scss';

type LegSource = 'booking' | 'calendar_setting' | 'user_said';

interface UpcomingStop {
  timezone: string;
  city: string;
  startsAt: string;
  endsAt: string | null;
}

// Mirrors GET /travel-timezone in dream-be (routes/travel-timezone.ts).
interface TravelTimezoneResponse {
  success: boolean;
  enabled: boolean;
  /** e.g. "Home: London. Travelling: Tokyo until Sun 12th (from your booking)." */
  line: string | null;
  home: { timezone: string; city: string } | null;
  travelling: {
    timezone: string;
    city: string;
    endsAt: string | null;
    source: LegSource;
  } | null;
  upcoming?: UpcomingStop[];
}

interface ZoneOption {
  zone: string;
  city: string;
  region: string;
}

const MAX_MATCHES = 6;

const cityOf = (zone: string) =>
  (zone.split('/').pop() ?? zone).replace(/_/g, ' ');

const regionOf = (zone: string) =>
  zone.split('/').slice(0, -1).join(' / ').replace(/_/g, ' ');

const allZones = (): string[] => {
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[];
  };
  return intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
};

const clock = (zone: string, now: Date) =>
  now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: zone,
  });

const day = (iso: string, zone: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: zone,
  });

/** The last full day in a place whose stay ends at `iso`. */
const lastDay = (iso: string, zone: string) =>
  day(new Date(Date.parse(iso) - 1).toISOString(), zone);

const SOURCE_TEXT: Record<LegSource, string> = {
  booking: 'from your booking',
  calendar_setting: 'from your Google Calendar',
  user_said: 'because you told me',
};

const useNow = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);
  return now;
};

interface CityPickerProps {
  zones: ZoneOption[];
  initial: string;
  now: Date;
  busy: boolean;
  onCancel: () => void;
  onSave: (zone: string) => void;
}

const CityPicker: FC<CityPickerProps> = ({
  zones,
  initial,
  now,
  busy,
  onCancel,
  onSave,
}) => {
  const listId = useId();
  const [query, setQuery] = useState(initial ? cityOf(initial) : '');
  const [chosen, setChosen] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return zones
      .filter(
        (z) =>
          z.city.toLowerCase().includes(q) ||
          z.zone.toLowerCase().includes(q.replace(/ /g, '_')),
      )
      .sort(
        (a, b) =>
          Number(!a.city.toLowerCase().startsWith(q)) -
          Number(!b.city.toLowerCase().startsWith(q)),
      )
      .slice(0, MAX_MATCHES);
  }, [query, zones]);

  const showList = !chosen && matches.length > 0;

  const choose = (option: ZoneOption) => {
    setChosen(option.zone);
    setQuery(option.city);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
      return;
    }
    if (!showList) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, matches.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = matches[activeIndex];
      if (option) choose(option);
    }
  };

  return (
    <div className='travel-tz-picker'>
      <label className='travel-tz-label' htmlFor={`${listId}-input`}>
        Home time zone
      </label>
      <input
        id={`${listId}-input`}
        className='travel-tz-input'
        role='combobox'
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete='list'
        aria-activedescendant={
          showList ? `${listId}-${activeIndex}` : undefined
        }
        placeholder='Type a city…'
        autoComplete='off'
        autoFocus
        value={query}
        disabled={busy}
        onChange={(e) => {
          setQuery(e.target.value);
          setChosen(null);
          setActiveIndex(0);
        }}
        onKeyDown={onKeyDown}
      />
      {showList && (
        <ul className='travel-tz-menu' id={listId} role='listbox'>
          {matches.map((option, index) => (
            <li
              key={option.zone}
              id={`${listId}-${index}`}
              role='option'
              aria-selected={index === activeIndex}
              className={index === activeIndex ? 'active' : undefined}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(option);
              }}
            >
              <span>{option.city}</span>
              <span className='travel-tz-menu-meta'>
                {option.region} · {clock(option.zone, now)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {query.trim() && !chosen && matches.length === 0 && (
        <p className='travel-tz-hint'>No city matches “{query.trim()}”.</p>
      )}
      <div className='travel-tz-actions'>
        <button
          type='button'
          className='travel-tz-pill'
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type='button'
          className='travel-tz-pill accent'
          onClick={() => chosen && onSave(chosen)}
          disabled={busy || !chosen || chosen === initial}
        >
          Save
        </button>
      </div>
    </div>
  );
};

const TravelTimezoneSection: FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const now = useNow();
  const [data, setData] = useState<TravelTimezoneResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [editingHome, setEditingHome] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const zones = useMemo<ZoneOption[]>(
    () =>
      allZones()
        .filter((z) => z.includes('/'))
        .map((zone) => ({ zone, city: cityOf(zone), region: regionOf(zone) }))
        .sort((a, b) => a.city.localeCompare(b.city)),
    [],
  );

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const load = useCallback(async () => {
    try {
      const res = await api.get<TravelTimezoneResponse>(
        '/travel-timezone',
        await authHeaders(),
      );
      setData(res.data);
    } catch (err) {
      console.error('Error fetching travel time zone:', err);
    }
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const run = useCallback(
    async (write: () => Promise<unknown>) => {
      setBusy(true);
      setError(null);
      try {
        await write();
        setEditingHome(false);
        await load();
      } catch (err) {
        console.error('Error updating time zone:', err);
        setError("Couldn't save that — please try again.");
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  const handleImHome = () =>
    run(async () => api.post('/travel-timezone/home', {}, await authHeaders()));

  const handleSaveHome = (timezone: string) =>
    run(async () =>
      api.put('/travel-timezone/home-zone', { timezone }, await authHeaders()),
    );

  if (!data?.enabled || !data.home) return null;

  const { home, travelling } = data;
  const upcoming = data.upcoming ?? [];
  const lastStop = upcoming[upcoming.length - 1];
  const tripEnd = lastStop ? lastStop.endsAt : (travelling?.endsAt ?? null);

  const tripSummary = () => {
    if (!travelling) return '';
    const parts: string[] = [];
    const next = upcoming[0];
    if (next)
      parts.push(`Then ${next.city} from ${day(next.startsAt, next.timezone)}`);
    else if (travelling.endsAt)
      parts.push(`Until ${lastDay(travelling.endsAt, travelling.timezone)}`);
    if (tripEnd) parts.push(`home ${day(tripEnd, home.timezone)}`);
    else parts.push('no end date yet');
    return `${parts.join(' · ')} (${SOURCE_TEXT[travelling.source]})`;
  };

  const stops = travelling
    ? [travelling.city, ...upcoming.map((s) => s.city), home.city]
    : [];

  return (
    <div className='daily-briefing-section travel-timezone-section'>
      <div className='daily-briefing-section-header'>
        <h2 className='section-title'>Time zone</h2>
        <p className='daily-briefing-section-subtitle'>
          Dream uses this for your briefing, routines and meetings.
        </p>
      </div>
      <div className='daily-briefing-section-card travel-tz-card'>
        {editingHome ? (
          <CityPicker
            zones={zones}
            initial={home.timezone}
            now={now}
            busy={busy}
            onCancel={() => setEditingHome(false)}
            onSave={handleSaveHome}
          />
        ) : (
          <>
            {travelling && (
              <>
                <div className='travel-tz-banner' role='status'>
                  <span className='travel-tz-banner-icon' aria-hidden='true'>
                    ✈
                  </span>
                  <div>
                    <div className='travel-tz-title'>
                      Using {travelling.city} time ·{' '}
                      {clock(travelling.timezone, now)}
                    </div>
                    <div className='travel-tz-banner-detail'>
                      {tripSummary()}
                    </div>
                  </div>
                </div>
                <ol className='travel-tz-timeline' aria-label='Trip stops'>
                  {stops.map((city, index) => (
                    <li
                      key={`${city}-${index}`}
                      className={index === 0 ? 'current' : undefined}
                    >
                      <span className='travel-tz-timeline-city'>{city}</span>
                      {index < stops.length - 1 && (
                        <span
                          className='travel-tz-timeline-seg'
                          aria-hidden='true'
                        />
                      )}
                    </li>
                  ))}
                </ol>
              </>
            )}
            <div className='travel-tz-row'>
              <div>
                <div className='travel-tz-title'>Home time zone</div>
                <div className='travel-tz-detail'>
                  {home.city} · {clock(home.timezone, now)} now
                </div>
              </div>
              <div className='travel-tz-row-actions'>
                <button
                  type='button'
                  className='travel-tz-link'
                  onClick={() => setEditingHome(true)}
                  disabled={busy}
                  aria-label='Change home time zone'
                >
                  Change
                </button>
                {travelling && (
                  <button
                    type='button'
                    className='travel-tz-pill'
                    onClick={handleImHome}
                    disabled={busy}
                  >
                    I'm home
                  </button>
                )}
              </div>
            </div>
            {!travelling && (
              <p className='travel-tz-hint'>
                Travelling? I'll ask when you land, or just tell me (“I'm in New
                York till Friday”).
              </p>
            )}
          </>
        )}
        {error && (
          <p className='travel-tz-error' role='alert'>
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default TravelTimezoneSection;
